import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, User, Mail, Phone, MoreHorizontal } from 'lucide-react';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';

interface Client {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  type: string;
  status: string;
}

export function CRMClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-display font-black uppercase text-ink italic">Gestión de Clientes</h1>
            <p className="text-serif italic text-ink/60 mt-2">Control total de tus leads y prospectos.</p>
          </div>
          <Button className="h-14 px-8 gap-2">
            <Plus className="h-5 w-5" />
            NUEVO CLIENTE
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-ink/30" />
          <Input 
            placeholder="BUSCAR POR NOMBRE O EMAIL..." 
            className="pl-12 h-16 text-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="h-48 brutalist-border bg-ink/5 animate-pulse" />
            ))
          ) : filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <div key={client._id} className="brutalist-border bg-parchment p-6 group hover:bg-ink hover:text-parchment transition-colors">
                <div className="flex justify-between items-start mb-6">
                  <div className="w-12 h-12 bg-terracotta flex items-center justify-center">
                    <User className="h-6 w-6 text-parchment" />
                  </div>
                  <button className="text-ink/30 group-hover:text-parchment/30">
                    <MoreHorizontal className="h-6 w-6" />
                  </button>
                </div>
                <h3 className="text-xl font-display font-black uppercase mb-4 italic truncate">{client.name}</h3>
                <div className="space-y-2 text-sm font-serif italic opacity-70">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {client.email}
                  </div>
                  {client.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {client.phone}
                    </div>
                  )}
                </div>
                <div className="mt-6 pt-6 border-t border-ink/10 group-hover:border-parchment/10 flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase tracking-widest bg-terracotta text-parchment px-2 py-1">
                    {client.type}
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-widest opacity-40">
                    {client.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center brutalist-border bg-ink/5 italic text-ink/40">
              No se encontraron clientes que coincidan con tu búsqueda.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
